import type { RealtimeChannel } from "@supabase/supabase-js";

import { supabase } from "./supabase";

export type TeamMessage = {
  id: string;
  team_id: string;
  sender_id: string;
  body: string;
  created_at: string;
  /** Display name resolved from user_profiles. */
  sender_name: string | null;
  /** Avatar URL resolved from user_profiles. */
  sender_avatar_url: string | null;
  /** When set, this message is replying to another. */
  reply_to_message_id: string | null;
  /** Lightweight preview of the parent for quoted-reply rendering. Null
   *  when the parent has been deleted or wasn't fetched. */
  reply_to: ReplyTo | null;
  /** Soft-delete tombstone fields. When deleted_at is set, the message
   *  is rendered as a grey ghost ("Message deleted by X"). */
  deleted_at: string | null;
  deleted_by_user_id: string | null;
  /** Display name of the user who deleted (resolved from user_profiles).
   *  Null when not deleted or when the deleter's profile isn't fetched. */
  deleted_by_name: string | null;
};

export type ReplyTo = {
  id: string;
  sender_id: string;
  sender_name: string | null;
  body_preview: string;
  deleted: boolean;
};

export type PinnedMessage = {
  team_id: string;
  message_id: string;
  pinned_by: string;
  pinned_at: string;
  /** Hydrated from team_messages for display. */
  message: TeamMessage | null;
};

type RawTeamMessage = {
  id: string;
  team_id: string;
  sender_id: string;
  body: string;
  created_at: string;
  reply_to_message_id: string | null;
  deleted_at: string | null;
  deleted_by_user_id: string | null;
};

type RawPinRow = {
  team_id: string;
  message_id: string;
  pinned_by: string;
  pinned_at: string;
};

const SELECT_COLS =
  "id, team_id, sender_id, body, created_at, reply_to_message_id, deleted_at, deleted_by_user_id";

/** Fetches the most recent N messages for a team, oldest first. Hydrates
 *  reply parents (one extra round-trip if any messages have replies) so
 *  the UI can render a quoted preview without an N+1 fetch. */
export async function listTeamMessages(teamId: string, limit = 100): Promise<TeamMessage[]> {
  const { data, error } = await supabase
    .from("team_messages")
    .select(SELECT_COLS)
    .eq("team_id", teamId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error || !data) return [];

  const rows = (data as RawTeamMessage[]).slice().reverse();
  return hydrateMessages(rows);
}

/** Hydrates raw rows with sender profiles + reply parents in two batched
 *  round-trips. Used by listTeamMessages and the realtime path. */
async function hydrateMessages(rows: RawTeamMessage[]): Promise<TeamMessage[]> {
  if (rows.length === 0) return [];

  const senderIds = [...new Set(rows.map((r) => r.sender_id))];

  const inWindow = new Map(rows.map((r) => [r.id, r] as const));
  const parentIdsToFetch = new Set<string>();
  for (const r of rows) {
    if (r.reply_to_message_id && !inWindow.has(r.reply_to_message_id)) {
      parentIdsToFetch.add(r.reply_to_message_id);
    }
  }

  let parentRows: RawTeamMessage[] = [];
  if (parentIdsToFetch.size > 0) {
    const { data: parents } = await supabase
      .from("team_messages")
      .select(SELECT_COLS)
      .in("id", [...parentIdsToFetch]);
    parentRows = (parents ?? []) as RawTeamMessage[];
  }
  for (const p of parentRows) senderIds.push(p.sender_id);
  // Also resolve the user who deleted each tombstoned row so the UI can
  // render "Message deleted by Luke" instead of just "Message deleted".
  for (const r of rows) {
    if (r.deleted_by_user_id) senderIds.push(r.deleted_by_user_id);
  }
  for (const p of parentRows) {
    if (p.deleted_by_user_id) senderIds.push(p.deleted_by_user_id);
  }
  const profileById = await fetchProfiles([...new Set(senderIds)]);

  const parentById = new Map<string, RawTeamMessage>();
  for (const p of parentRows) parentById.set(p.id, p);

  return rows.map((row) => toHydrated(row, profileById, parentById, inWindow));
}

function toHydrated(
  row: RawTeamMessage,
  profileById: Map<string, { full_name: string | null; avatar_url: string | null }>,
  parentById: Map<string, RawTeamMessage>,
  inWindow: Map<string, RawTeamMessage>
): TeamMessage {
  const p = profileById.get(row.sender_id);
  const replyTo: ReplyTo | null = row.reply_to_message_id
    ? buildReplyPreview(row.reply_to_message_id, profileById, parentById, inWindow)
    : null;
  const deleter = row.deleted_by_user_id
    ? profileById.get(row.deleted_by_user_id)
    : null;
  return {
    ...row,
    sender_name: p?.full_name ?? null,
    sender_avatar_url: p?.avatar_url ?? null,
    reply_to: replyTo,
    deleted_by_name: deleter?.full_name ?? null,
  };
}

function buildReplyPreview(
  parentId: string,
  profileById: Map<string, { full_name: string | null; avatar_url: string | null }>,
  parentById: Map<string, RawTeamMessage>,
  inWindow: Map<string, RawTeamMessage>
): ReplyTo {
  const parent = parentById.get(parentId) ?? inWindow.get(parentId);
  if (!parent) {
    return { id: parentId, sender_id: "", sender_name: null, body_preview: "", deleted: true };
  }
  const isDeleted = parent.deleted_at != null;
  const senderProf = profileById.get(parent.sender_id);
  return {
    id: parent.id,
    sender_id: parent.sender_id,
    sender_name: senderProf?.full_name ?? null,
    body_preview: isDeleted
      ? ""
      : (parent.body ?? "").slice(0, 140).replace(/\s+/g, " ").trim(),
    deleted: isDeleted,
  };
}

async function fetchProfiles(
  ids: string[]
): Promise<Map<string, { full_name: string | null; avatar_url: string | null }>> {
  const result = new Map<string, { full_name: string | null; avatar_url: string | null }>();
  if (ids.length === 0) return result;
  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, full_name, avatar_url")
    .in("id", ids);
  for (const p of profiles ?? []) {
    result.set(p.id as string, {
      full_name: (p as { full_name: string | null }).full_name ?? null,
      avatar_url: (p as { avatar_url: string | null }).avatar_url ?? null,
    });
  }
  return result;
}

export async function sendTeamMessage(
  teamId: string,
  body: string,
  options?: { replyToMessageId?: string | null }
): Promise<{ message: TeamMessage | null; error: Error | null }> {
  const trimmed = body.trim();
  if (!trimmed) return { message: null, error: new Error("Message is empty.") };
  if (trimmed.length > 2000) return { message: null, error: new Error("Message too long (max 2000 chars).") };

  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) {
    return { message: null, error: new Error(userErr?.message ?? "Not signed in") };
  }

  const insert: Record<string, unknown> = {
    team_id: teamId,
    sender_id: userData.user.id,
    body: trimmed,
  };
  if (options?.replyToMessageId) insert.reply_to_message_id = options.replyToMessageId;

  const { data, error } = await supabase
    .from("team_messages")
    .insert(insert)
    .select(SELECT_COLS)
    .single();

  if (error || !data) return { message: null, error: new Error(error?.message ?? "Send failed") };

  const [hydrated] = await hydrateMessages([data as RawTeamMessage]);
  return { message: hydrated ?? null, error: null };
}

/** Soft-deletes a message via the SECURITY DEFINER `delete_team_message`
 *  RPC. The RPC enforces "only sender or captain". The row remains in
 *  the table, with deleted_at set; UI must render it as a ghost row. */
export async function deleteTeamMessage(messageId: string): Promise<{ ok: boolean; error: Error | null }> {
  const { error } = await supabase.rpc("delete_team_message", { p_message_id: messageId });
  if (error) return { ok: false, error: new Error(error.message) };
  return { ok: true, error: null };
}

/** Lists currently pinned messages for a team, hydrated. Returns pin
 *  metadata + the pinned message (or null when the underlying message
 *  was deleted). Newest pins first. */
export async function listPinnedMessages(teamId: string): Promise<PinnedMessage[]> {
  const { data: pinRows } = await supabase
    .from("team_pinned_messages")
    .select("team_id, message_id, pinned_by, pinned_at")
    .eq("team_id", teamId)
    .order("pinned_at", { ascending: false });
  const rows = (pinRows ?? []) as RawPinRow[];
  if (rows.length === 0) return [];

  const { data: msgs } = await supabase
    .from("team_messages")
    .select(SELECT_COLS)
    .in("id", rows.map((r) => r.message_id));
  const hydrated = await hydrateMessages((msgs ?? []) as RawTeamMessage[]);
  const byId = new Map(hydrated.map((m) => [m.id, m] as const));

  return rows.map((r) => ({
    team_id: r.team_id,
    message_id: r.message_id,
    pinned_by: r.pinned_by,
    pinned_at: r.pinned_at,
    message: byId.get(r.message_id) ?? null,
  }));
}

export async function pinTeamMessage(messageId: string): Promise<{ ok: boolean; error: Error | null }> {
  const { error } = await supabase.rpc("pin_team_message", { p_message_id: messageId });
  if (error) return { ok: false, error: new Error(error.message) };
  return { ok: true, error: null };
}

export async function unpinTeamMessage(messageId: string): Promise<{ ok: boolean; error: Error | null }> {
  const { error } = await supabase.rpc("unpin_team_message", { p_message_id: messageId });
  if (error) return { ok: false, error: new Error(error.message) };
  return { ok: true, error: null };
}

/** Subscribe to pin/unpin/delete signals for a team. Fires `onChange`
 *  whenever the pinned list could have changed (insert/delete on the
 *  pin table) OR a team_messages UPDATE happens (covers soft-delete
 *  flipping deleted_at). Caller is responsible for debouncing. */
export function subscribeToTeamMessageEdits(
  teamId: string,
  onChange: () => void
): () => void {
  const suffix =
    typeof globalThis !== "undefined" && typeof globalThis.crypto !== "undefined"
      ? Math.random().toString(36).slice(2, 10)
      : String(Date.now());
  const channel = supabase
    .channel(`team_msg_edits:${teamId}:${suffix}`)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "team_pinned_messages", filter: `team_id=eq.${teamId}` },
      () => onChange()
    )
    .on(
      "postgres_changes",
      { event: "DELETE", schema: "public", table: "team_pinned_messages", filter: `team_id=eq.${teamId}` },
      () => onChange()
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "team_messages", filter: `team_id=eq.${teamId}` },
      () => onChange()
    )
    .subscribe();
  return () => {
    try {
      void supabase.removeChannel(channel);
    } catch {
      // already removed - ignore
    }
  };
}

/**
 * Subscribes to new messages for a team. Returns an unsubscribe function.
 * The handler receives fully-hydrated messages (with sender name/avatar).
 */
export function subscribeToTeamMessages(
  teamId: string,
  onMessage: (msg: TeamMessage) => void
): () => void {
  // Unique suffix per call. Realtime errors out with "cannot add
  // postgres_changes callbacks after subscribe()" when a channel topic
  // is reused while a prior one is still in the subscribed state - which
  // happens easily in dev when the team page double-mounts or the
  // student bounces between the workspace and the team page. Adding a
  // session-unique suffix means every subscribe owns a fresh channel.
  const suffix =
    typeof globalThis !== "undefined" && typeof globalThis.crypto !== "undefined"
      ? Math.random().toString(36).slice(2, 10)
      : String(Date.now());
  const channel: RealtimeChannel = supabase
    .channel(`team_messages:${teamId}:${suffix}`)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "team_messages", filter: `team_id=eq.${teamId}` },
      async (payload) => {
        const row = payload.new as RawTeamMessage;
        const [hydrated] = await hydrateMessages([row]);
        if (hydrated) onMessage(hydrated);
      }
    )
    .subscribe();

  return () => {
    try {
      void supabase.removeChannel(channel);
    } catch {
      // already removed - ignore
    }
  };
}

// ===========================================================================
// Per-(team, student) read-pointer helpers - backed by team_members.last_read_at
// so it is correct across devices and across two test accounts on the same
// browser profile (where the old localStorage approach collided).
// ===========================================================================

export async function getMyLastReadAt(teamId: string): Promise<string | null> {
  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase
    .from("team_members")
    .select("last_read_at")
    .eq("team_id", teamId)
    .eq("student_id", uid)
    .maybeSingle();
  if (error || !data) return null;
  return (data as { last_read_at: string | null }).last_read_at ?? null;
}

export async function markTeamRead(teamId: string, iso: string): Promise<void> {
  const { error } = await supabase.rpc("mark_team_read", {
    p_team_id: teamId,
    p_iso: iso,
  });
  if (error && typeof console !== "undefined") {
    console.warn("[markTeamRead] failed:", error.message);
  }
}

export async function getMyTeamUnreadCount(): Promise<number> {
  const { data, error } = await supabase.rpc("get_my_team_unread_count");
  if (error || data == null) return 0;
  const n = typeof data === "number" ? data : Number(data);
  return Number.isFinite(n) ? n : 0;
}

/** Per-team unread count — used by the program-scoped sidebar badge so
 *  the "Team" tab in competition X only lights up for new messages in
 *  the team the student is in for X (not the total across all teams). */
export async function getTeamUnreadCount(teamId: string): Promise<number> {
  const { data, error } = await supabase.rpc("get_team_unread_count", {
    p_team_id: teamId,
  });
  if (error || data == null) return 0;
  const n = typeof data === "number" ? data : Number(data);
  return Number.isFinite(n) ? n : 0;
}

export function subscribeToTeamUnreadSignals(onChange: () => void): () => void {
  // Unique suffix per call so multiple subscribers (e.g. the global
  // shell hook AND the competition page's per-team effect) don't collide
  // on the same channel topic — Supabase realtime errors with
  // "cannot add postgres_changes callbacks after subscribe()" otherwise.
  const suffix =
    typeof globalThis !== "undefined" && typeof globalThis.crypto !== "undefined"
      ? Math.random().toString(36).slice(2, 10)
      : String(Date.now());
  const channel = supabase
    .channel(`team-unread-signals:${suffix}`)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "team_messages" },
      () => onChange()
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "team_members" },
      () => onChange()
    )
    .subscribe();
  return () => {
    try {
      void supabase.removeChannel(channel);
    } catch {
      // already removed
    }
  };
}
